/* ImmHome swipeable product gallery
   - Touch swipe (native scroll-snap) + mouse drag on desktop
   - Overlay prev/next arrows, dots, image counter
   - Keeps thumbnails, Dawn media-gallery.js and product-info.js fully compatible
   - Re-initialises inside the Shopify theme editor on section reload
*/
(() => {
  'use strict';

  const DRAG_THRESHOLD = 8;

  /* Keep the sticky gallery offset in sync with the real site header height. */
  const syncHeaderHeight = () => {
    if (!document.body.classList.contains('template-product')) return;
    const header = document.querySelector('.imm-site-header');
    if (!header) return;
    const isSticky = header.classList.contains('is-sticky');
    const height = isSticky ? Math.round(header.getBoundingClientRect().height) : 0;
    document.body.style.setProperty('--imm-header-height', `${height}px`);
  };

  const getSlides = (track) =>
    Array.from(track.querySelectorAll(':scope > li[data-media-id]'));

  const currentIndex = (track) => {
    const slides = getSlides(track);
    if (!slides.length) return 0;
    const width = track.clientWidth || 1;
    const index = Math.round(track.scrollLeft / width);
    return Math.max(0, Math.min(index, slides.length - 1));
  };

  const scrollToIndex = (track, index, smooth = true) => {
    const slides = getSlides(track);
    if (!slides.length) return;
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    const target = slides[clamped];
    track.scrollTo({ left: target.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
  };

  const initGallery = (gallery) => {
    if (!gallery || gallery.dataset.immSwipeReady === 'true') return;
    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    const track = viewer?.querySelector('ul');
    if (!viewer || !track) return;
    gallery.dataset.immSwipeReady = 'true';

    const prevButton = viewer.querySelector('[data-imm-swipe-prev]');
    const nextButton = viewer.querySelector('[data-imm-swipe-next]');
    const counterCurrent = viewer.querySelector('[data-imm-swipe-current]');
    const counterTotal = viewer.querySelector('[data-imm-swipe-total]');
    const dotsWrap = viewer.querySelector('[data-imm-swipe-dots]');
    const thumbsWrap = gallery.querySelector('[id^="GalleryThumbnails-"]');
    const dragEnabled = gallery.dataset.immDrag !== 'false';

    const buildDots = () => {
      if (!dotsWrap) return;
      const slides = getSlides(track);
      dotsWrap.innerHTML = '';
      if (slides.length < 2) return;
      slides.forEach((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'imm-swipe-dot';
        dot.setAttribute('aria-label', `Go to image ${index + 1}`);
        dot.addEventListener('click', () => scrollToIndex(track, index));
        dotsWrap.appendChild(dot);
      });
    };

    const syncState = () => {
      const slides = getSlides(track);
      const index = currentIndex(track);
      const activeSlide = slides[index];

      if (counterCurrent) counterCurrent.textContent = String(index + 1);
      if (counterTotal) counterTotal.textContent = String(slides.length);

      if (dotsWrap) {
        const dots = dotsWrap.querySelectorAll('.imm-swipe-dot');
        if (dots.length !== slides.length) buildDots();
        dotsWrap.querySelectorAll('.imm-swipe-dot').forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === index);
        });
      }

      if (prevButton) prevButton.classList.toggle('is-disabled', index <= 0);
      if (nextButton) nextButton.classList.toggle('is-disabled', index >= slides.length - 1);

      /* Sync active thumbnail + keep it visible inside the thumb strip. */
      if (activeSlide && thumbsWrap) {
        const targetId = activeSlide.dataset.mediaId;
        const thumbItem = thumbsWrap.querySelector(`[data-target="${targetId}"]`);
        if (thumbItem) {
          thumbsWrap.querySelectorAll('.thumbnail[aria-current]').forEach((thumb) => {
            if (thumb.closest('[data-target]') !== thumbItem) thumb.removeAttribute('aria-current');
          });
          thumbItem.querySelector('.thumbnail')?.setAttribute('aria-current', 'true');
          const list = thumbItem.parentElement;
          if (list && list.scrollWidth > list.clientWidth) {
            const left = thumbItem.offsetLeft - list.clientWidth / 2 + thumbItem.clientWidth / 2;
            list.scrollTo({ left, behavior: 'smooth' });
          }
        }
      }
    };

    let syncFrame = 0;
    const scheduleSync = () => {
      cancelAnimationFrame(syncFrame);
      syncFrame = requestAnimationFrame(syncState);
    };

    track.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', scheduleSync, { passive: true });

    prevButton?.addEventListener('click', (event) => {
      event.preventDefault();
      scrollToIndex(track, currentIndex(track) - 1);
    });
    nextButton?.addEventListener('click', (event) => {
      event.preventDefault();
      scrollToIndex(track, currentIndex(track) + 1);
    });

    /* Keyboard support when the viewer has focus. */
    viewer.setAttribute('tabindex', '0');
    viewer.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollToIndex(track, currentIndex(track) - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollToIndex(track, currentIndex(track) + 1);
      }
    });

    /* Mouse drag-to-swipe (touch swiping is native). */
    if (dragEnabled) {
      let dragging = false;
      let startX = 0;
      let startScroll = 0;
      let moved = 0;

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        moved = 0;
        startX = event.clientX;
        startScroll = track.scrollLeft;
        track.classList.add('is-dragging');
      });

      track.addEventListener('pointermove', (event) => {
        if (!dragging || event.pointerType !== 'mouse') return;
        const delta = event.clientX - startX;
        moved = Math.max(moved, Math.abs(delta));
        if (moved > DRAG_THRESHOLD) {
          track.setPointerCapture?.(event.pointerId);
          event.preventDefault();
        }
        track.scrollLeft = startScroll - delta;
      });

      const endDrag = (event) => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
        if (event?.pointerId != null && track.hasPointerCapture?.(event.pointerId)) {
          track.releasePointerCapture(event.pointerId);
        }
        scrollToIndex(track, currentIndex(track));
      };

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') endDrag(event);
      });

      /* Stop the drag from triggering the lightbox click. */
      track.addEventListener(
        'click',
        (event) => {
          if (moved > DRAG_THRESHOLD) {
            event.preventDefault();
            event.stopPropagation();
            moved = 0;
          }
        },
        { capture: true }
      );

      track.addEventListener('dragstart', (event) => event.preventDefault());
    }

    buildDots();
    syncState();
  };

  const initAll = () => {
    syncHeaderHeight();
    document
      .querySelectorAll('.imm-product-gallery media-gallery[data-imm-swipe]')
      .forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  window.addEventListener('load', syncHeaderHeight);
  window.addEventListener('resize', () => {
    window.clearTimeout(window.__immHeaderSyncTimer);
    window.__immHeaderSyncTimer = window.setTimeout(syncHeaderHeight, 120);
  });

  /* Theme editor: sections are re-rendered on setting changes. */
  document.addEventListener('shopify:section:load', () => window.setTimeout(initAll, 20));
})();
